import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, type ErrorComponentProps } from '@tanstack/react-router'
import { useId } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import { companyDetailQueryOptions } from '@/features/company/api/queries'
import { useSignalPerformance } from '@/features/signals/api/queries'
import { formatDate } from '@/lib/format'
import EquityGraph from '@/screens/signals/components/EquityGraph'
import EquityTable from '@/screens/signals/components/EquityTable'
import SignalCandleChart from '@/screens/signals/components/SignalCandleChart'
import SignalStatsTiles from '@/screens/signals/components/SignalStatsTiles'
import SignalStockHeader from '@/screens/signals/components/SignalStockHeader'
import SignalStockViewLink from '@/screens/signals/components/SignalStockViewLink'
import {
  SignalStockBodySkeleton,
  SignalStockHeaderSkeleton,
} from '@/screens/signals/components/SignalStockSkeletons'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsFootnote from '@/screens/signals/components/SignalsFootnote'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import {
  SIGNAL_STOCK_TABS,
  loadSignalStock,
  signalStockSearchSchema,
  signalSymbolOf,
  type SignalStockSource,
} from '@/screens/signals/SignalStockScreen.loader'

/** Where Back goes: the board the member opened the page from. */
const BACK = {
  live: '/market/signals/live',
  daily: '/market/signals/daily',
} as const satisfies Record<SignalStockSource, string>

/** The board's own name, for the not-found page's way back. */
const BOARD = { live: 'Live signals', daily: 'Daily forecast' } as const

const TAB_OPTIONS: readonly TabBarOption[] = [
  { value: 'stats', label: 'Signal Statistics' },
  { value: 'trades', label: 'Equity Table' },
  { value: 'equity', label: 'Equity Graph' },
]

/** The words under the figures: what the equity assumes (owner brief, 2026-10-16). */
const ASSUMPTIONS =
  'Every closed signal of this stock since it listed. The whole balance goes into each signal, with no fees.'

/** The URL's ticker as the page shows it, valid or not: the pending and error frames print it. */
const shownSymbol = (raw: string): string => signalSymbolOf(raw) ?? raw.trim().toUpperCase()

/*
 * The loaded page, block for block: the header with the URL's ticker, the
 * tab row, the view the URL opens on, the two note lines and the candle
 * chart (`SignalStockSkeletons.tsx`).
 */
function SignalStockPending() {
  const { symbol } = Route.useParams()
  const { tab, from } = Route.useSearch()
  const shown = shownSymbol(symbol)
  return (
    <ScreenLayout
      pending
      pendingLabel={`Loading signals for ${shown}…`}
      header={
        <SignalStockHeaderSkeleton
          symbol={shown}
          back={BACK[from]}
          action={<SignalStockViewLink to="technicals" symbol={shown} from={from} />}
        />
      }
      {...signalsRailProps(shown)}
    >
      <SignalStockBodySkeleton tab={tab} />
    </ScreenLayout>
  )
}

function SignalStockError(props: ErrorComponentProps) {
  const { symbol } = Route.useParams()
  const { from } = Route.useSearch()
  const shown = shownSymbol(symbol)
  return (
    <SignalsRouteError
      {...props}
      header={<TitleRow title={shown} back={BACK[from]} />}
      pageTitle={shown}
      title={`Signals for ${shown} did not load`}
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/stock/$symbol')({
  validateSearch: signalStockSearchSchema,
  loader: loadSignalStock,
  pendingComponent: SignalStockPending,
  errorComponent: SignalStockError,
  component: SignalStockScreen,
})

/**
 * Not a symbol mm-be lists: an invalid path, or one the performance read
 * answered not-found. The way back is the board it came from.
 */
function SignalStockNotFound({ symbol, from }: { symbol: string; from: SignalStockSource }) {
  return (
    <ScreenLayout
      header={<TitleRow title={symbol} back={BACK[from]} />}
      {...signalsRailProps(symbol)}
    >
      <EmptyState
        title="Stock not found"
        description={`No listed company has the symbol ${symbol}.`}
        action={
          <Link to={BACK[from]} className={stateActionClass('page')}>
            Back to {BOARD[from]}
          </Link>
        }
      />
    </ScreenLayout>
  )
}

/**
 * Signal Zone · Signal page (f21–f23) — one stock's signals since it listed:
 * twelve statistics, the equity table and the equity graph under underline
 * tabs (`?tab=`), the notes, and the stock's candle chart with the
 * dashboard's layer chips under every view.
 *
 * MAIN read keyed on the path's symbol alone, so suspense (the loader always
 * has it): the stock's performance, whose figures, trades and starting
 * balance mm-be works out — the page computes none of them. The tab and the
 * table's page are the client's, so a change re-renders and reads nothing.
 * The company read fills the header's name and sector. A 403 is the locked
 * state and any other failure the route's error page (`SignalsRouteError`);
 * no closed signal is an empty state in place of the view, with the chart
 * still drawn.
 */
function SignalStockScreen() {
  const { symbol: raw } = Route.useParams()
  const { from } = Route.useSearch()
  const symbol = signalSymbolOf(raw)
  // Before any suspense read: the loader asked for nothing for an invalid symbol.
  if (symbol === null) return <SignalStockNotFound symbol={shownSymbol(raw)} from={from} />
  return <SignalStockPage symbol={symbol} />
}

function SignalStockPage({ symbol }: { symbol: string }) {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const idBase = useId()
  const performance = useSignalPerformance(symbol).data
  const company = useQuery({ ...companyDetailQueryOptions(symbol), enabled: performance !== null })

  if (performance === null) {
    return <SignalStockNotFound symbol={symbol} from={search.from} />
  }

  const header = (
    <SignalStockHeader
      symbol={symbol}
      back={BACK[search.from]}
      name={company.data?.name ?? null}
      sector={company.data?.sector ?? null}
      logo={company.data?.profile.logoUrl ?? null}
      identityPending={company.data === undefined && !company.isError}
      action={<SignalStockViewLink to="technicals" symbol={symbol} from={search.from} />}
    />
  )

  const { trades, stats, startingBalance, openPosition } = performance
  const hasTrades = trades.length > 0

  const view = !hasTrades ? (
    <SignalsEmpty
      reason="list"
      size="section"
      title={`No closed signals for ${symbol} yet`}
      description={
        openPosition === null
          ? 'A signal closes when a red signal follows a green one.'
          : `Its green signal, opened on ${formatDate(openPosition.entryDate)}, is still open.`
      }
    />
  ) : search.tab === 'stats' ? (
    <SignalStatsTiles stats={stats} />
  ) : search.tab === 'trades' ? (
    <EquityTable
      trades={trades}
      startingBalance={startingBalance}
      page={search.page}
      pageSize={search.pageSize}
      onPageChange={(page) => {
        void navigate({ search: (prev) => ({ ...prev, page }) })
      }}
      onPageSizeChange={(pageSize) => {
        void navigate({ search: (prev) => ({ ...prev, pageSize, page: 1 }) })
      }}
    />
  ) : (
    <EquityGraph symbol={symbol} trades={trades} startingBalance={startingBalance} />
  )

  return (
    <ScreenLayout header={header} {...signalsRailProps(symbol)}>
      <TabBar
        ariaLabel={`${symbol} signal views`}
        options={TAB_OPTIONS}
        value={search.tab}
        idBase={idBase}
        onChange={(value) => {
          const tab = SIGNAL_STOCK_TABS.find((option) => option === value) ?? 'stats'
          void navigate({ search: (prev) => ({ ...prev, tab }) })
        }}
      />
      <TabPanel idBase={idBase} value={search.tab}>
        {view}
      </TabPanel>
      {hasTrades ? (
        <div className="flex flex-col gap-1">
          <p className="text-body-xs text-muted-foreground-subtle">{ASSUMPTIONS}</p>
          <SignalsFootnote basis="history" />
        </div>
      ) : null}
      <section aria-label={`${symbol} price chart`}>
        <SignalCandleChart symbol={symbol} name={symbol} date={null} />
      </section>
    </ScreenLayout>
  )
}
